/* eslint-disable @typescript-eslint/no-use-before-define */
import { useMemo, useState } from 'react';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';

import { Button, GoogleCaptcha, ValidationAlert, YandexCaptcha, CaptchaErrorAlert } from 'components';
import OtpStep from './components/OtpStep';
import TotpStep from './components/TotpStep';
import CredentialsStep from './components/CredentialsStep';

import useValidation from 'hooks/forms/useValidation';
import useNotifications from 'hooks/common/useNotifications';
import useGoogleCaptcha from 'hooks/captcha/google/useGoogleCaptcha';
import useYandexCaptcha from 'hooks/captcha/yandex/useYandexCaptcha';
import useGoogleCaptchaHandler from 'hooks/captcha/google/useGoogleCaptchaHandler';

import auth from 'services/auth.service';
import { checkIsLimitError } from './utils/errors';
import UrlRestoringService from 'services/urlRestoring.service';
import checkIsCaptchaFinalError from 'utils/captcha/google/checkIsCaptchaFinalError';

import { CAPTCHA_TYPE } from 'config';
import { Y_CAPTCHA_PARAM_KEY } from 'constants/captcha/yandex';
import { CredentialErrors, ErrorMessagesMap } from './constants/errors';
import { ErrorMessages, ErrorType, GoogleCaptchaErrorType, RequestErrorMessages } from 'constants/errors';

import { CaptchaType } from 'types/captcha';
import { ErrorResponseType } from 'types/request';
import { ILoginFormParams, LoginStep } from './LoginForm.types';

import './LoginForm.styles.scss';

const LoginForm = () => {
  const rule = useValidation();
  const { t } = useTranslation();
  const { notificationApi } = useNotifications();

  const [form] = Form.useForm<ILoginFormParams>();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [step, setStep] = useState(LoginStep.CREDENTIALS);

  const { onCaptchaError: onGoogleCaptchaError } = useGoogleCaptchaHandler({ setError });

  const googleCaptcha = useGoogleCaptcha('login', { disabled: CAPTCHA_TYPE !== CaptchaType.GOOGLE });
  const captchaV2 = Form.useWatch(['captchaV2'], form);

  const captchaSteps = [LoginStep.CREDENTIALS];
  const isCaptchaStep = captchaSteps.includes(step);

  const onValidationError = () => {
    form.setFields([
      { name: 'email', errors: [''] },
      { name: 'password', errors: [''] },
    ]);
  };

  const showErrorNotification = () => {
    notificationApi.error({ title: t(ErrorMessages.COMMON.UNKNOWN) });
    form.validateFields();
  };

  const redirectUser = () => {
    window.location.href = UrlRestoringService.getUrl();
  };

  const resetGoogleCaptchaField = () => {
    form.setFieldValue('captchaV2', null);
  };

  const handleResetGoogleCaptcha = () => {
    googleCaptcha.resetCaptchaError();
    resetGoogleCaptchaField();
  };

  const login = async (formData: ILoginFormParams) => {
    setUserInfo(formData);

    const result = (await auth.login(formData)) as unknown as Record<string, unknown>;

    if (result?.key === ErrorType.REQUIRED_2FA) {
      setStep(LoginStep.TOTP);
      setUserInfo({ ...formData, token: result?.token });

      switch (CAPTCHA_TYPE) {
        case CaptchaType.YANDEX:
          yandexCaptcha.onResetCaptcha();
          break;

        case CaptchaType.GOOGLE:
        default:
          handleResetGoogleCaptcha();
      }
    } else {
      redirectUser();
    }

    setError(null);
  };

  const loginTotp = async (formData: ILoginFormParams) => {
    const { totp } = formData;
    const { email, password, token } = userInfo || {};

    await auth.login2FA({ email, password, totp, token });
    redirectUser();
  };

  const loginOtp = async (formData: ILoginFormParams) => {
    const { otp } = formData;
    const { email, password, token } = userInfo || {};

    await auth.recoveryLogin({ email, password, otp, token });
    redirectUser();
  };

  const onLogin = async ({ captchaV2, ...formData }: ILoginFormParams) => {
    setLoading(true);

    let body = formData;

    if (CAPTCHA_TYPE === CaptchaType.GOOGLE) {
      const captchaParams = isCaptchaStep ? googleCaptcha.getCaptchaParams(captchaV2) : {};
      body = { ...formData, ...captchaParams };
    }

    try {
      if (step === LoginStep.CREDENTIALS) {
        return await login(body);
      }

      if (step === LoginStep.TOTP) {
        return await loginTotp(body);
      }

      if (step === LoginStep.OTP) {
        return await loginOtp(body);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const yandexCaptcha = useYandexCaptcha({
    onChangeError: setError,
    onSubmit: onLogin,
  });

  const handleError = (error: ErrorResponseType) => {
    const errorType = error?.body?.key || error?.body?.message;
    const isCredentialsError = CredentialErrors.includes(errorType);

    const isLimitError = checkIsLimitError(error);

    switch (CAPTCHA_TYPE) {
      case CaptchaType.YANDEX: {
        const isCaptchaError = yandexCaptcha.checkIsCaptchaError(errorType);

        yandexCaptcha.onChangeVisible(false);

        if (isCaptchaError) {
          setError(errorType);
          return;
        }

        yandexCaptcha.onResetCaptcha();
        break;
      }

      case CaptchaType.GOOGLE:
      default: {
        const captchaError = googleCaptcha.checkCaptchaError(error);
        const isCaptchaFinalError = checkIsCaptchaFinalError(error);

        googleCaptcha.resetCaptchaState();

        if (isCaptchaFinalError) {
          notificationApi.error({ title: t('requestErrors.captcha2Failed') });
          resetGoogleCaptchaField();
          return;
        }

        if (captchaError) {
          onGoogleCaptchaError(captchaError);
          resetGoogleCaptchaField();
          return;
        }
      }
    }

    if (isCredentialsError) {
      form.setFields([
        { name: 'email', errors: [''] },
        { name: 'password', errors: [''] },
      ]);
      setError(ErrorType.INVALID_EMAIL);
      return;
    }

    if (isLimitError) {
      notificationApi.error({ title: t('requestErrors.tooManyRequests') });
      setError(null);
      return;
    }

    setError(null);
    notificationApi.error({
      title: errorType
        ? t(RequestErrorMessages[errorType] || ErrorMessages.COMMON.UNKNOWN)
        : t(ErrorMessages.COMMON.UNKNOWN),
    });
  };

  const handleFinish = (values: ILoginFormParams) => {
    switch (CAPTCHA_TYPE) {
      case CaptchaType.YANDEX:
        return isCaptchaStep ? yandexCaptcha.onSubmit(values) : onLogin(values);

      case CaptchaType.GOOGLE:
      default:
        return onLogin(values);
    }
  };

  const showGoogleCaptcha = CAPTCHA_TYPE === CaptchaType.GOOGLE && googleCaptcha.isCaptchaError && isCaptchaStep;
  const showYandexCaptcha = CAPTCHA_TYPE === CaptchaType.YANDEX && isCaptchaStep;

  const isYandexCaptchaError = yandexCaptcha.checkIsCaptchaError(error);
  const isGoogleCaptchaError = error === ErrorType.CAPTCHA_FINAL_ERROR;

  const showCaptchaErrorAlert = isYandexCaptchaError || isGoogleCaptchaError;

  const isSubmitButtonDisabled = useMemo(() => {
    if (!isCaptchaStep) {
      return false;
    }

    switch (CAPTCHA_TYPE) {
      case CaptchaType.YANDEX:
        return isYandexCaptchaError;

      case CaptchaType.GOOGLE:
      default:
        return (
          (googleCaptcha.captchaError && googleCaptcha.captchaError === ErrorType.CAPTCHA_FINAL_ERROR) ||
          (googleCaptcha.captchaError === GoogleCaptchaErrorType.FIRST_STEP_FAILED && !captchaV2)
        );
    }
  }, [isCaptchaStep, isYandexCaptchaError, googleCaptcha.captchaError, captchaV2]);

  return (
    <Form
      form={form}
      layout="vertical"
      name="login"
      onFinish={handleFinish}
      requiredMark="optional"
      scrollToFirstError
      className="np-login-form"
    >
      {step === LoginStep.CREDENTIALS && <CredentialsStep loading={loading} />}
      {step === LoginStep.TOTP && <TotpStep loading={loading} />}
      {step === LoginStep.OTP && <OtpStep loading={loading} />}

      {showGoogleCaptcha && (
        <Form.Item name="captchaV2" validateFirst rules={[rule.required]}>
          <GoogleCaptcha key={googleCaptcha.key} />
        </Form.Item>
      )}

      {showYandexCaptcha && (
        <YandexCaptcha
          key={yandexCaptcha.key}
          visible={yandexCaptcha.visible}
          onChange={yandexCaptcha.onChange}
          onSuccess={token => onLogin({ ...form.getFieldsValue(), [Y_CAPTCHA_PARAM_KEY]: token })}
          onChangeStatus={yandexCaptcha.onChangeStatus}
          onChangeVisible={yandexCaptcha.onChangeVisible}
        />
      )}

      {showCaptchaErrorAlert && <CaptchaErrorAlert type={error} className="np-login-form__alert" />}

      <ValidationAlert
        error={error}
        marginBottom={24}
        messages={ErrorMessagesMap}
        onShowAlert={onValidationError}
        onHideAlert={showErrorNotification}
      />

      <Form.Item>
        <Button htmlType="submit" block loading={loading} disabled={isSubmitButtonDisabled}>
          {t('form.button.signIn')}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default LoginForm;
